import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Event {
  id: string
  title: string
  description: string
  event_type: string
  status: string
  venue_name: string
  venue_city: string
  start_date: string
  end_date: string
  capacity: number
  registered_count: number
  ticket_price: number
  is_virtual: boolean
}

interface Speaker {
  id: string
  name: string
  title: string
  company: string
  bio: string
}

export default function EventsPage() {
  const [events, setEvents] = useState<Event[]>([])
  const [speakers, setSpeakers] = useState<Speaker[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [eventsRes, speakersRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/events/'),
      fetch(API_BASE + '/events/speakers'),
      fetch(API_BASE + '/events/statistics')
    ])
    if (eventsRes.ok) setEvents((await eventsRes.json()).events || [])
    if (speakersRes.ok) setSpeakers((await speakersRes.json()).speakers || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live': return 'bg-red-100 text-red-800'
      case 'upcoming': return 'bg-green-100 text-green-800'
      case 'registration_open': return 'bg-blue-100 text-blue-800'
      case 'completed': return 'bg-gray-100 text-gray-600'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Event Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Create Event</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Events</div>
              <div className="text-2xl font-bold">{statistics.total_events}</div>
              <div className="text-xs text-green-600">{statistics.upcoming_events} upcoming</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Registrations</div>
              <div className="text-2xl font-bold">{statistics.total_registrations}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Revenue</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_revenue)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Attendance</div>
              <div className="text-2xl font-bold">{statistics.average_attendance_rate}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-4">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Upcoming Events</h2>
              <div className="space-y-4">
                {events.slice(0, 6).map(event => (
                  <div key={event.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold">{event.title}</h3>
                        <div className="text-sm text-gray-500">{event.description}</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(event.status)}`}>
                        {event.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-3">
                      <span>{formatDate(event.start_date)}</span>
                      <span>•</span>
                      {event.is_virtual ? (
                        <span className="text-purple-600">Virtual Event</span>
                      ) : (
                        <span>{event.venue_name}, {event.venue_city}</span>
                      )}
                      <span>•</span>
                      <span>{event.registered_count}/{event.capacity} registered</span>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="w-full max-w-xs bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min((event.registered_count / event.capacity) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-bold text-green-600 ml-4">
                        {event.ticket_price > 0 ? formatCurrency(event.ticket_price) : 'Free'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Speakers</h3>
            <div className="space-y-3">
              {speakers.slice(0, 8).map(speaker => (
                <div key={speaker.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                    {speaker.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{speaker.name}</div>
                    <div className="text-xs text-gray-500">{speaker.title}</div>
                    <div className="text-xs text-blue-600">{speaker.company}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
